/**
 * exercise-view-base.js
 */

import React from 'react';
import {
  Typography,
  Box,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import IconSection from 'components/icon-section';
import InfoRow from 'components/info-row';
import DetectionOdorsView from 'components/detection-odors/view';
import { getPatrolIconName, PatrolTypeTitle } from '../../training-utils';


export default function ExerciseViewBase(props) {
  const {
    exerciseRecord,
    showName
  } = props;
  const { t } = useTranslation('general');

  // exercise
  const workMode = exerciseRecord && exerciseRecord.workMode;
  const workModeName = workMode && Types.WorkMode.getName(t, workMode);
  const patrolTypes = exerciseRecord?.patrol?.patrolTypes;

  return (
    <>

      {/* general info */}
      <IconSection iconName='summary'>
        {showName &&
        <InfoRow title="Name" text={exerciseRecord.name} />}
        <InfoRow title="Type" text={workModeName} />
        <InfoRow title="Monitor" text={exerciseRecord.monitor} />
        {workMode === Types.WorkMode.DETECTION &&
        <InfoRow title="Controlled Negative" text={exerciseRecord.isControlledNegative ? 'Yes' : 'No'} />}
      </IconSection>

      {exerciseRecord.workMode === Types.WorkMode.DETECTION &&
      <IconSection iconName='detection'>
        {/* detection: environments + odors */}
        <DetectionOdorsView
          environments={exerciseRecord?.detection?.environments}
          recordType={Types.Record.EXERCISE}
        />

        {/* detection: odor placement */}
        {(exerciseRecord.detection.odorPlacedBy || exerciseRecord.detection.odorPlacementTime) &&
        <Box sx={{ mt: 2 }}>
          {exerciseRecord.detection.odorPlacedBy && 
          <InfoRow title='Odors Placed By' text={exerciseRecord.detection.odorPlacedBy} />}
          {exerciseRecord.detection.odorPlacementTime && 
          <InfoRow title='Odors Placed On' text={format(parseISO(exerciseRecord.detection.odorPlacementTime), 'MM/dd/yyyy h:mm aaa')} />}
        </Box>}
      </IconSection>}

      {/* patrol: list patrol types */}
      {patrolTypes?.map((patrolRecord, i) => (
        <IconSection 
          key={patrolRecord.id}
          iconName={getPatrolIconName(patrolRecord.type)}
        >
          <PatrolTypeTitle 
            patrolType={patrolRecord.type} 
            patrolTypes={patrolTypes} 
            step={i + 1}
          />

          {/* building search */}
          {patrolRecord.type === Types.Patrol.BUILDING_SEARCH &&
          <Box sx={{ mt: 0.5 }}>
            <InfoRow showEmptyDefault title='Building Types' text={patrolRecord.buildingSearch?.types.map(type => type.customOtherName || type.name).join(', ')} />
            {patrolRecord.buildingSearch?.areaSize &&
              <InfoRow showEmptyDefault title='Area Size' text={`${patrolRecord.buildingSearch?.areaSize || ''} ${patrolRecord.buildingSearch?.areaSizeUnit?.name || ''}`} />
            }
            <InfoRow showEmptyDefault title='Decoy Count' text={patrolRecord.buildingSearch?.decoyCount || ''} />
          </Box>}

          {/* human search */}
          {patrolRecord.type === Types.Patrol.HUMAN_SEARCH &&
          <Box sx={{ mt: 0.5 }}>
            <InfoRow showEmptyDefault title='Terrain Types' text={patrolRecord.humanSearch?.types.map(type => type.customOtherName || type.name).join(', ')} />
            <InfoRow showEmptyDefault title='Decoy Count' text={patrolRecord.humanSearch?.decoyCount} />
          </Box>}
        </IconSection>
      ))}

      {/* description && goal */}
      <IconSection iconName='description'>
        {/* description */}
        {exerciseRecord.description &&
        <>
          <Typography variant='subtitle2' style={{ marginBottom: 4 }}>Description</Typography>
          <Typography 
            variant="body2" 
            style={{ marginBottom: 16, whiteSpace: 'pre-wrap' }} 
            data-testid='exercise-description'
          >
            {exerciseRecord.description}
          </Typography>
        </>}

        {/* goal */}
        <Typography variant='subtitle2' style={{ marginBottom: 4 }}>Goal</Typography>
        <Typography 
          variant="body2" 
          style={{ whiteSpace: 'pre-wrap' }} 
          data-testid='exercise-goal'
        >
          {exerciseRecord.goal}
        </Typography>
      </IconSection>

    </>
  );
}